import React, { useMemo, useState } from "react";
import {
   Chart as ChartJS,
   CategoryScale,
   LinearScale,
   PointElement,
   LineElement,
   ArcElement,
   Filler,
   Tooltip,
   Legend,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
   CategoryScale,
   LinearScale,
   PointElement,
   LineElement,
   ArcElement,
   Filler,
   Tooltip,
   Legend,
);


const TRIP_OVERVIEW_FALLBACK = {
   weekly: [
      { label: "Mon", trips: 8 },
      { label: "Tue", trips: 12 },
      { label: "Wed", trips: 7 },
      { label: "Thu", trips: 15 },
      { label: "Fri", trips: 10 },
      { label: "Sat", trips: 6 },
      { label: "Sun", trips: 9 },
   ],
   monthly: [
      { label: "Week 1", trips: 32 },
      { label: "Week 2", trips: 45 },
      { label: "Week 3", trips: 38 },
      { label: "Week 4", trips: 52 },
   ],
};

const STATUS_COLORS = {
   "completed": "#22c55e",
   "in_progress": "#3b82f6",
   "pending": "#8b5cf6",
   "rejected": "#f97316",
   "cancelled": "#f97316",
};

const STATUS_DATA_FALLBACK = [
   { label: "Completed", value: 14, color: "#22c55e", percent: 58 },
   { label: "In Progress", value: 6, color: "#3b82f6", percent: 25 },
   { label: "Pending", value: 2, color: "#8b5cf6", percent: 8 },
   { label: "Cancelled", value: 2, color: "#f97316", percent: 8 },
];

const lineOptions = {
   responsive: true,
   maintainAspectRatio: false,
   tension: 0.4,
   plugins: {
      legend: { display: false },
      tooltip: {
         backgroundColor: "#0f2744",
         titleFont: { size: 12 },
         bodyFont: { size: 12 },
         padding: 10,
         cornerRadius: 8,
      },
   },
   scales: {
      x: {
         grid: { display: false },
         ticks: { color: "#94a3b8", font: { size: 11 } },
         border: { display: false },
      },
      y: {
         min: 0,
         max: 25,
         ticks: {
            stepSize: 5,
            color: "#94a3b8",
            font: { size: 11 },
            callback: (value) => value,
         },
         title: {
            display: true,
            text: "Trips",
            color: "#94a3b8",
            font: { size: 11, weight: "500" },
            align: "end",
         },
         grid: {
            color: "#eef2f7",
            drawBorder: false,
         },
         border: { display: false },
      },
   },
};

const doughnutOptions = {
   responsive: true,
   maintainAspectRatio: false,
   cutout: "72%",
   plugins: {
      legend: { display: false },
      tooltip: {
         backgroundColor: "#0f2744",
         padding: 10,
         cornerRadius: 8,
      },
   },
};

const DashboardTripCharts = ({ tripOverview, tripStatus, range, setRange }) => {
   const dynamicStatusData = useMemo(() => {
      if (tripStatus?.breakdown) {
         return tripStatus.breakdown.map((item) => ({
            label: item.label,
            value: item.count,
            percent: item.percentage,
            color: STATUS_COLORS[item.status] || "#cbd5e1"
         }));
      }
      return STATUS_DATA_FALLBACK;
   }, [tripStatus]);

   const OverviewTripSummery = [
      {
         label: "Total Trips",
         value: tripOverview?.summary?.total_trips || tripOverview?.summery?.total_trips || 0,
         tone: "blue",
      },
      {
         label: "Completed",
         value: tripOverview?.summary?.completed || tripOverview?.summery?.completed_trips || 0,
         tone: "green",
      },
      {
         label: "In Progress",
         value: tripOverview?.summary?.in_progress || tripOverview?.summery?.in_progress_trips || 0,
         tone: "orange",
      },
      {
         label: "Pending",
         value: tripOverview?.summary?.pending || tripOverview?.summery?.pending_trips || 0,
         tone: "purple",
      },
   ];

 const lineData = useMemo(() => {
   const backendChartData = tripOverview?.chart_data;

   const hasBackendData =
      Array.isArray(backendChartData) && backendChartData.length > 0;

   const chartData = hasBackendData
      ? backendChartData
      : TRIP_OVERVIEW_FALLBACK[range] || TRIP_OVERVIEW_FALLBACK.weekly;

   return {
      labels: chartData.map((item) => item.label),
      datasets: [
         {
            fill: true,
            data: chartData.map((item) => item.trips),
            borderColor: "#3b82f6",
            backgroundColor: (context) => {
               const chart = context.chart;
               const { ctx, chartArea } = chart;

               if (!chartArea) {
                  return "rgba(59, 130, 246, 0.12)";
               }

               const gradient = ctx.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom
               );

               gradient.addColorStop(
                  0,
                  "rgba(59, 130, 246, 0.28)"
               );

               gradient.addColorStop(
                  1,
                  "rgba(59, 130, 246, 0.02)"
               );

               return gradient;
            },
            borderWidth: 2.5,
            pointRadius: 4,
            pointHoverRadius: 5,
            pointBackgroundColor: "#3b82f6",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
         },
      ],
   };
}, [range, tripOverview]);

   const doughnutData = useMemo(
      () => ({
         labels: dynamicStatusData.map((item) => item.label),
         datasets: [
            {
               data: dynamicStatusData.map((item) => item.value),
               backgroundColor: dynamicStatusData.map((item) => item.color),
               borderWidth: 0,
               hoverOffset: 4,
            },
         ],
      }),
      [dynamicStatusData],
   );

   const monthLineOptions = useMemo(
      () => ({
         ...lineOptions,
         scales: {
            ...lineOptions.scales,
            y: {
               ...lineOptions.scales.y,
               max: range === "week" ? 25 : 60,
               ticks: {
                  ...lineOptions.scales.y.ticks,
                  stepSize: range === "week" ? 5 : 10,
               },
            },
         },
      }),
      [range],
   );

   return (
      <div className="row oil-chart-row">
         <div className="col-xl-7 col-lg-12">
            <div className="oil-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Trip Overview</h3>
                  <div className="oil-range-toggle" role="tablist">
                     <button
                        type="button"
                        className={`oil-range-toggle__btn ${
                           range === "weekly" ? "is-active" : ""
                        }`}
                        onClick={() => setRange("weekly")}
                     >
                        This Week
                     </button>
                     <button
                        type="button"
                        className={`oil-range-toggle__btn ${
                           range === "monthly" ? "is-active" : ""
                        }`}
                        onClick={() => setRange("monthly")}
                     >
                        This Month
                     </button>
                  </div>
               </div>

               <div className="oil-panel__chart oil-panel__chart--line">
                  <Line options={monthLineOptions} data={lineData} />
               </div>

               <div className="oil-overview-stats">
                  {OverviewTripSummery.map((item, index) => (
                     <div
                        key={index}
                        className={`oil-overview-stat oil-overview-stat--${item.tone}`}
                     >
                        <div className="oil-overview-stat__value">{item.value}</div>
                        <div className="oil-overview-stat__label">{item.label}</div>
                     </div>
                  ))}
               </div>
            </div>
         </div>

         <div className="col-xl-5 col-lg-12">
            <div className="oil-panel">
               <div className="oil-panel__header">
                  <h3 className="oil-panel__title">Trip Status</h3>
               </div>

               <div className="oil-status-layout">
                  <div className="oil-status-donut">
                     <Doughnut options={doughnutOptions} data={doughnutData} />
                     <div className="oil-status-donut__center">
                        <div className="oil-status-donut__total">{tripStatus?.total_trips || 24}</div>
                        <div className="oil-status-donut__caption">Total Trips</div>
                     </div>
                  </div>

                  <ul className="oil-status-legend">
                     {dynamicStatusData.map((item) => (
                        <li key={item.label} className="oil-status-legend__item">
                           <span className="oil-status-legend__left">
                              <span
                                 className="oil-status-legend__dot"
                                 style={{ backgroundColor: item.color }}
                              />
                              <span className="oil-status-legend__label">
                                 {item.label}
                              </span>
                           </span>
                           <span className="oil-status-legend__value">
                              {item.value} ({item.percent}%)
                           </span>
                        </li>
                     ))}
                  </ul>
               </div>
            </div>
         </div>
      </div>
   );
};

export default DashboardTripCharts;
